/**
 * Widget B: AUM by Product Type
 * 
 * Displays a donut chart showing the distribution of Assets Under Management (AUM)
 * across different product types: TSP, 401(k), 403(b), and Annuity.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateAUMByProduct, AUMByProductData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR, PASTEL_COLORS } from "./widgetColors";
import { ChartTooltip, CHART_MARGINS, CHART_ANIMATION, renderPieLabel } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface AUMByProductWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

// Use unified colors consistently
const COLORS = {
  tsp: UNIFIED_CHART_COLORS[1],        // Sky blue (index 1)
  "401k": UNIFIED_CHART_COLORS[2],     // Lavender (index 2)
  "403b": UNIFIED_CHART_COLORS[7],    // Rose pink (index 7)
  annuity: UNIFIED_CHART_COLORS[3],   // Peach (index 3)
};

const chartConfig = {
  tsp: {
    label: "TSP",
    color: COLORS.tsp,
  },
  "401k": {
    label: "401(k)",
    color: COLORS["401k"],
  },
  "403b": {
    label: "403(b)",
    color: COLORS["403b"],
  },
  annuity: {
    label: "Annuity",
    color: COLORS.annuity,
  },
};

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

export function AUMByProductWidget({
  clients,
  isLoading = false,
  userId,
}: AUMByProductWidgetProps) {
  const api = XanoAPI.getInstance();

  // Fetch data from API endpoint
  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["aum-by-product", userId],
    queryFn: async () => {
      const result = await api.getAUMByProduct(userId);
      console.log("🔍 AUMByProductWidget: API response:", result);
      console.log("🔍 AUMByProductWidget: API response type:", typeof result);
      console.log("🔍 AUMByProductWidget: Has products?", !!result?.products);
      console.log("🔍 AUMByProductWidget: Products count:", result?.products?.length);
      console.log("🔍 AUMByProductWidget: Grand total:", result?.grandTotal);
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const data = useMemo(() => {
    // Try API data first, then fallback to client-based calculation
    let result: AUMByProductData;
    
    console.log("🔍 AUMByProductWidget: Processing data - apiData:", apiData);
    console.log("🔍 AUMByProductWidget: apiData is null?", apiData === null);
    console.log("🔍 AUMByProductWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 AUMByProductWidget: apiData type:", typeof apiData);
    console.log("🔍 AUMByProductWidget: Clients available:", clients.length);
    
    // Check if API data has valid non-zero values
    const hasValidApiData = apiData && 
      apiData.products !== undefined && 
      apiData.grandTotal !== undefined &&
      apiData.grandTotal > 0;
    
    console.log("🔍 AUMByProductWidget: hasValidApiData?", hasValidApiData);
    console.log("🔍 AUMByProductWidget: apiData.grandTotal:", apiData?.grandTotal);
    console.log("🔍 AUMByProductWidget: clients.length:", clients.length);
    
    if (hasValidApiData) {
      // API data is available and has values - use it
      console.log("✅ AUMByProductWidget: Using API data");
      console.log("🔍 AUMByProductWidget: Products array:", apiData.products);
      console.log("🔍 AUMByProductWidget: Products length:", apiData.products?.length);
      console.log("🔍 AUMByProductWidget: Grand total:", apiData.grandTotal);
      result = apiData as AUMByProductData;
    } else if (clients.length > 0) {
      // API data is empty or unavailable - try calculating from client data
      console.log("⚠️ AUMByProductWidget: API data empty or unavailable, calculating from client data");
      console.log("🔍 AUMByProductWidget: Calculating AUM for", clients.length, "clients");
      try {
        const calculatedData = calculateAUMByProduct(clients);
        console.log("✅ AUMByProductWidget: Calculated AUM from clients:", calculatedData);
        if (calculatedData.grandTotal > 0) {
          result = calculatedData;
        } else {
          console.log("⚠️ AUMByProductWidget: Calculated AUM is also zero, returning empty structure");
          result = {
            products: [],
            grandTotal: 0,
          };
        }
      } catch (error) {
        console.error("❌ AUMByProductWidget: Error calculating AUM from clients:", error);
        result = {
          products: [],
          grandTotal: 0,
        };
      }
    } else {
      // No API data and no clients - return empty structure
      console.log("❌ AUMByProductWidget: No API data and no clients available, returning empty structure");
      console.log("🔍 AUMByProductWidget: apiData value:", apiData);
      result = {
        products: [],
        grandTotal: 0,
      };
    }
    
    // Transform to chart data format
    const chartData = result.products
      .filter((p) => p.amount > 0)
      .map((product) => {
        const productKey = product.product as keyof typeof COLORS;
        const fillColor = COLORS[productKey] || UNIFIED_CHART_COLORS[0]; // Fallback to first color if product type not found
        return {
          name: product.product === "tsp" ? "TSP" : 
                product.product === "401k" ? "401(k)" :
                product.product === "403b" ? "403(b)" : "Annuity",
          value: product.amount,
          percentage: product.percentage ?? 0,
          fill: fillColor,
          key: product.product,
        };
      });

    return { chartData, result };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.result.grandTotal === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">
              No AUM data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div className="h-[340px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_MARGINS}>
              <Pie
                data={data.chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => {
                  const percentage = entry.percentage !== undefined 
                    ? entry.percentage 
                    : (entry.payload?.percentage !== undefined ? entry.payload.percentage : 0);
                  if (percentage === undefined || isNaN(percentage) || percentage < 5) return "";
                  return (
                    <text
                      x={entry.x}
                      y={entry.y}
                      fill="hsl(var(--foreground))"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="13"
                      fontWeight="600"
                      style={{ 
                        textShadow: "0 1px 2px rgba(0,0,0,0.1), 0 0 4px rgba(255,255,255,0.8)",
                        filter: "drop-shadow(0 0 2px rgba(255,255,255,0.9))"
                      }}
                    >
                      {(percentage ?? 0).toFixed(0)}%
                    </text>
                  );
                }}
                outerRadius={95}
                innerRadius={60}
                paddingAngle={3}
                dataKey="value"
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell 
                    key={entry.key || `cell-${index}`} 
                    fill={entry.fill}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    opacity={0.85}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value) => [
                    formatCurrency(value),
                    data.chartData.find(d => d.value === value)?.name || ""
                  ]}
                />} 
                cursor={{ fill: "transparent" }}
              />
              <Legend
                verticalAlign="bottom"
                height={60}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ paddingTop: "15px" }}
                formatter={(value, entry: any) => {
                  const dataPoint = data.chartData.find((d) => d.key === entry.payload?.key);
                  if (!dataPoint || dataPoint.percentage === undefined) return value;
                  return (
                    <span className="text-xs text-muted-foreground">
                      {value} <span className="font-semibold text-foreground">{formatCurrency(dataPoint.value)}</span> ({(dataPoint.percentage ?? 0).toFixed(1)}%)
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Center label showing grand total */}
          <div 
            className="absolute pointer-events-none"
            style={{
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <div className="text-center">
              <div className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wide">
                Total AUM
              </div>
              <div 
                className="text-lg font-bold"
                style={{ 
                  color: PASTEL_COLORS.teal,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                }}
              >
                {formatCurrency(data.result.grandTotal)}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item, index) => (
            <div key={item.key || `aum-product-${index}`} className="space-y-1">
              <div className="text-sm font-bold" style={{ color: item.fill }}>
                {formatCurrency(item.value)}
              </div>
              <div className="text-xs font-medium text-foreground">{item.name}</div>
              <div className="text-xs text-muted-foreground">
                {(item.percentage ?? 0).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

